// smsGateway — live status of the SIM SMS device (RUT241), feature C.
//
// The outbound-sms-queue endpoint stamps settings/smsGateway.lastSeen on every
// poll (the claim proves the router is online). The Text Inbox subscribes here
// to show a green / amber / red device dot. settings/* is client-readable, so no
// Netlify function is needed. Until the SIM gateway is actually running the dot
// reads "offline" — which is the truth.

import { db } from './firebase';
import { USE_SUPABASE, supabase } from './supabaseClient';

export type GatewayStatus = 'online' | 'idle' | 'offline' | 'stalled';

/** A Text Inbox device as people call it. The queue names them 'smseagle', 'android' (the dispatch phone,
 *  sms-gate.app, 2026-10-26) or — a caller that says nothing — the retired 'rut241'. */
export function deviceLabel(name: string | null | undefined): string {
  if (name === 'smseagle') return 'SMSEagle';
  if (name === 'android') return 'Android phone';
  return 'RUT241';
}

const ONLINE_MS = 60 * 1000;       // green: a heartbeat within the last minute
const IDLE_MS = 5 * 60 * 1000;     // amber: 1–5 min; red beyond that, or never

/** Classify a last-seen heartbeat. `now` is injectable for tests.
 *  `stalledSince` (settings/smsGateway, stamped server-side) means the router
 *  IS polling but the modem has not sent anything for 10+ min with work
 *  waiting — the wedge signature. It outranks 'online': a green dot while
 *  nothing goes out is the lie this exists to stop. */
export function gatewayStatus(lastSeen: number | null, now: number = Date.now(), stalledSince: number | null = null): GatewayStatus {
  if (!lastSeen) return 'offline';
  const age = now - lastSeen;
  if (stalledSince && age <= IDLE_MS) return 'stalled';
  if (age <= ONLINE_MS) return 'online';
  if (age <= IDLE_MS) return 'idle';
  return 'offline';
}

const noop = () => {};

/** Live-subscribe to the gateway's last-seen heartbeat (ms epoch, or null when
 *  it has never checked in) and its stall flag. Returns an unsubscribe fn. */
export function subscribeGatewayLastSeen(
  cb: (lastSeen: number | null, stalledSince?: number | null) => void,
  onError?: (e: Error) => void,
): () => void {
  // Supabase mode: no Firebase session, so the client can't read Firestore
  // settings directly. Poll the admin-SDK-backed status function instead —
  // the heartbeat granularity is minutes, so a 30s poll loses nothing.
  if (USE_SUPABASE) {
    let dead = false;
    const read = async () => {
      try {
        // authedFetch: a 401 here means the session was ended elsewhere —
        // it signs the tab out (→ login) instead of leaving a grey dot.
        const { authedFetch } = await import('./forwardedSmsApi');
        const res = await authedFetch('/.netlify/functions/sms-gateway-status');
        if (!res.ok) throw new Error(`sms-gateway-status ${res.status}`);
        const body = await res.json();
        if (!dead) {
          cb(
            typeof body.lastSeen === 'number' ? body.lastSeen : null,
            typeof body.stalledSince === 'number' ? body.stalledSince : null,
          );
        }
      } catch (e: any) {
        onError?.(e);
      }
    };
    void read();
    // The device stamps sms_gateway_events as it works, so a change there is
    // the signal that the heartbeat moved. No traffic, no calls.
    const off = onTableChange('sms_gateway_events', () => { void read(); }, 30_000); // its old cadence
    return () => { dead = true; off(); };
  }
  if (!db) {
    cb(null, null);
    return noop;
  }
  return db.collection('settings').doc('smsGateway').onSnapshot(
    (snap) => {
      const data = snap.data() as { lastSeen?: unknown; stalledSince?: unknown } | undefined;
      cb(
        typeof data?.lastSeen === 'number' ? data!.lastSeen : null,
        typeof data?.stalledSince === 'number' ? data!.stalledSince : null,
      );
    },
    (err) => {
      onError?.(err as Error);
    },
  );
}

// ── Queue diagnostics (Umer, 2026-08-26) ────────────────────────────────────
// Read-only snapshot from netlify/functions/sms-queue-status: what's waiting,
// what failed, and the recorded outage windows ("when did messages start
// sticking, what type, how many"). Fetched on demand by the gateway panel.

export type SmsQueueKind = 'text' | 'photo' | 'upload_link';

export interface SmsQueueRow {
  id: string;
  to: string | null;
  kind: SmsQueueKind;
  status: string;
  createdAt: number | null;
  attempts: number;
  error: string | null;
  source: string | null;
  preview?: string;
  segments?: number;
  part?: number | null;
  parts?: number | null;
}

export interface SmsGatewayOutage {
  id: string;
  /** 'outage' (router silent >5 min) or a hardening event: 'stalled',
   *  'modem_restart', 'modem_recovered', 'reboot', … (router watchdog), or
   *  'duplicate' — the same text went to one number twice within ten minutes
   *  (the queue's duplicate watch, 2026-09-21). */
  type?: string;
  loggedAt?: number;
  detail?: string | null;
  since?: number | null;
  downFrom?: number;
  downTo?: number;
  gapMs?: number;
  pendingCount?: number | null;
  oldestPendingAt?: number | null;
  kinds?: Partial<Record<SmsQueueKind, number>>;
}

/** How much actually went out recently — messages and the SMS PARTS they cost
 *  on the air, which is what the modem and the carrier count. */
export interface SmsThroughput {
  windowMs: number;
  lastMinute: { messages: number; parts: number };
  last5Minutes: { messages: number; parts: number };
  /** The device's rated ceiling (30 parts/min on one modem) to read it against. */
  ratedPartsPerMinute: number;
  /** Queued → delivered over the last hour, averaged per MESSAGE, so a quiet
   *  stretch is never averaged in as if it were slow. */
  hour: { messages: number; avgWaitMs: number | null; maxWaitMs: number | null };
  /** The same, in 10-minute buckets, newest first. An empty bucket has no
   *  average — it is a dash, not a zero. */
  buckets: Array<{ from: number; to: number; messages: number; avgWaitMs: number | null; maxWaitMs: number | null }>;
  bucketMs: number;
}

export interface SmsQueueStatus {
  ok: boolean;
  now: number;
  lastSeen: number | null;
  lastDeliveredAt: number | null;
  nextClaimAt?: number | null;
  throughput?: SmsThroughput | null;
  /** What the pacing ladder decided on the last send (_sms-segments.gapAfter). */
  lastGapMs?: number | null;
  lastDepth?: number | null;
  /** How many messages the polling device may hold at once: the SMSEagle's
   *  window (Admin Panel dial, 1..4) when it is the one polling, else one. */
  messagesPerCycle?: number;
  /** The window RIGHT NOW: one per `windowStep` waiting, up to the Admin
   *  Panel ceiling `smseagleWindowMax` (2026-09-21, dynamic by queue depth). */
  smseagleWindow?: number;
  smseagleWindowMax?: number;
  windowStep?: number;
  /** Which device made the last claim ('smseagle' | 'android' | 'rut241'), and when each
   *  was last heard from. A caller that does not name itself is the RUT241. */
  lastSeenBy?: string | null;
  devices?: Record<string, number>;
  /** Which ladder step is in effect: 1 = normal pacing, 4 = as fast as it goes. */
  paceStep?: number;
  stalledSince?: number | null;
  lastRouterEvent?: { type: string; detail: string | null; at: number } | null;
  maxAttempts: number;
  waiting: SmsQueueRow[];
  waitingCount: number;
  oldestWaitingAt: number | null;
  failed: SmsQueueRow[];
  events: SmsGatewayOutage[];
}


// ── change signals, so a card refetches instead of polling ─────────────────
// The queue card and the gateway heartbeat were on 20 s and 30 s timers: a
// Netlify call each tick, per open tab, forever, each one resolving the caller
// against GoTrue and the users table before reading anything. An idle line paid
// that all night for nothing.
//
// These subscribe to the table instead and fire when it actually changes. The
// authoritative read stays the server function (it aggregates and role-filters
// with the service key); realtime only says WHEN to run it. A slow safety
// interval remains so a dropped socket degrades to the old behaviour rather
// than a card frozen on stale numbers — minutes, not seconds.
// Two intervals, and which one is running is the answer to "is realtime
// actually delivering here?". It starts on the OLD polling cadence and only
// relaxes once a payload has genuinely arrived.
//
// That matters because a subscription can succeed and still deliver nothing:
// outbound_sms_queue needs both the realtime publication AND a select policy
// (migration 0046) — without them the channel joins happily and stays silent.
// Assuming realtime works would quietly drop the queue card to a 5-minute
// refresh, which is exactly when a dispatcher would not notice a stuck send.
// Self-tuning instead: correct before the migration, cheap after it, and it
// needs no redeploy when the migration lands.
const SAFETY_MS = 5 * 60 * 1000;  // once realtime has proven itself

/** Run `onSignal` when `table` changes, plus an interval as a safety net.
 *  `pollMs` is each caller's OWN pre-realtime cadence — what it polled at
 *  before, kept until a payload proves the channel delivers (seen on
 *  wolfdrivers.com: the gateway heartbeat had gone from 30 s to 20 s because
 *  one shared default was tighter than its old timer). Firestore mode has no
 *  realtime table here and keeps the interval alone. */
export function onTableChange(table: string, onSignalRaw: () => void, pollMs: number): () => void {
  // A hidden tab does no work for a screen nobody sees (Phase 1, 2026-09-30: every hidden driver, store and
  // dispatch tab kept calling the read-state / queue / gateway functions all day). A signal that arrives
  // while hidden is held, and runs once when the tab is shown again.
  let heldWhileHidden = false;
  const hidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';
  const onSignal = () => { if (hidden()) { heldWhileHidden = true; return; } onSignalRaw(); };
  const onVisible = () => { if (!hidden() && heldWhileHidden) { heldWhileHidden = false; onSignalRaw(); } };
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible);
  const stopVisible = () => { if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible); };
  let timer: ReturnType<typeof setInterval> | null = null;
  let live = false;
  const setInterval_ = (ms: number) => {
    if (timer) clearInterval(timer);
    timer = setInterval(onSignal, ms);
  };
  setInterval_(pollMs);
  if (!USE_SUPABASE) return () => { if (timer) clearInterval(timer); stopVisible(); };

  let debounce: ReturnType<typeof setTimeout> | null = null;
  const schedule = () => {
    if (!live) { live = true; setInterval_(SAFETY_MS); } // proof, not assumption
    if (debounce) clearTimeout(debounce);
    // A multi-part send writes several rows at once; one refetch covers them.
    debounce = setTimeout(onSignal, 400);
  };
  const channel = supabase()
    .channel(`gw_${table}_${Math.random().toString(36).slice(2, 8)}`)
    .on('postgres_changes', { event: '*', schema: 'public', table }, schedule)
    .subscribe();

  return () => {
    if (timer) clearInterval(timer);
    if (debounce) clearTimeout(debounce);
    stopVisible();
    supabase().removeChannel(channel);
  };
}

/** The outbound queue changed — the queue card should re-read its status. */
export function subscribeQueueChanges(onSignal: () => void): () => void {
  return onTableChange('outbound_sms_queue', onSignal, 20_000); // its old cadence
}

/** Stop a queued message before the device sends it (Umer, 2026-09-07). The
 *  row leaves the waiting list and the poller can never claim it. A long reply
 *  queued as several parts is cancelled as a whole (`alsoCanceled` counts the
 *  rest; 0 when the backend does not report it). `wasInFlight` means the
 *  device had already claimed it, so the text may have gone out. A message
 *  that finished in the meantime answers 409 with `already_sent` /
 *  `already_failed`, which the panel reports rather than treating as a fault. */
export async function cancelQueuedSms(id: string): Promise<{ ok: boolean; wasInFlight?: boolean; alsoCanceled?: number; reason?: string }> {
  const { authedFetch } = await import('./forwardedSmsApi');
  const res = await authedFetch('/.netlify/functions/sms-queue-cancel', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id }),
  });
  const body = await res.json().catch(() => ({}));
  if (res.ok) return { ok: true, wasInFlight: !!body.wasInFlight, alsoCanceled: Number(body.alsoCanceled) || 0 };
  if (res.status === 404 || res.status === 409) return { ok: false, reason: String(body.error || 'gone') };
  throw new Error(`sms-queue-cancel ${res.status}`);
}

export async function fetchQueueStatus(): Promise<SmsQueueStatus> {
  const { authedFetch } = await import('./forwardedSmsApi');
  const res = await authedFetch('/.netlify/functions/sms-queue-status');
  if (!res.ok) throw new Error(`sms-queue-status ${res.status}`);
  return res.json();
}
